import React, { useEffect, useState } from "react";
import { FiRefreshCw } from "react-icons/fi";
import { SelectOption } from "./AddUser.types";
import "./AddUser.css";
import Loader from "../Loader/Loader";

interface CheckboxGroupInputProps {
  label: string;
  name: string;
  formik: any;
  options: SelectOption[];
  required?: boolean;
  helperText?: string;
  // Shows a search box above the checkboxes that filters `options` locally
  // (debounced) — the full list is already loaded, so this never hits the
  // network, it just narrows what's shown.
  searchable?: boolean;
  searchPlaceholder?: string;
  // Shows a small refresh button next to the search box that re-fetches
  // `options` from its source (e.g. a client created moments ago via
  // "+ Create New Client" won't be in the list until refreshed).
  onRefresh?: () => void;
  loadingOption?: boolean;
}

const SEARCH_DEBOUNCE_MS = 500;

// Plain-click multi-select (no Ctrl/Cmd needed, unlike a native
// <select multiple>). formik.values[name] / initialValues[name] must be a
// string[] for this — see addUserInitialValues.client.
export const CheckboxGroupInput: React.FC<CheckboxGroupInputProps> = ({
  label,
  name,
  formik,
  options,
  required,
  helperText,
  searchable,
  searchPlaceholder = "Search...",
  onRefresh,
  loadingOption,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const selected: string[] = formik.values[name] ?? [];
  const error = formik.touched[name] && formik.errors[name];

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const visibleOptions = debouncedSearchTerm
    ? options.filter((option) =>
        option.label
          .toLowerCase()
          .includes(debouncedSearchTerm.toLowerCase()),
      )
    : options;

  const toggleOption = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    formik.setFieldValue(name, next);
    formik.validateField(name);
  };

  // Select All / Deselect All
const allVisibleSelected =
  visibleOptions.length > 0 &&
  visibleOptions.every((option) =>
    selected.includes(option.value),
  );

const handleSelectAll = () => {
  const visibleValues = visibleOptions.map(
    (option) => option.value,
  );

  if (allVisibleSelected) {
    // Deselect all visible users
    const next = selected.filter(
      (value) => !visibleValues.includes(value),
    );

    formik.setFieldValue(name, next);
  } else {
    // Select all visible users
    const next = [
      ...selected,
      ...visibleValues.filter(
        (value) => !selected.includes(value),
      ),
    ];

    formik.setFieldValue(name, next);
  }

  formik.validateField(name);
};

  return (
    <div>
      <label className="addUser-label">
        {label} {required && <span className="addUser-required">*</span>}
      </label>

      {helperText && <div className="addUser-helper-text">{helperText}</div>}

      {(searchable || onRefresh) && (
        <div className="addUser-search-row">
          {searchable && (
            <input
              type="text"
              className="addUser-search-input"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          )}

          {onRefresh && (
            <button
              type="button"
              className="addUser-refresh-btn"
              onClick={onRefresh}
              title="Refresh list"
            >
              <FiRefreshCw size={14} 
               className={loadingOption ? "refresh-spin" : ""}/>
            </button>
          )}
        </div>
      )}  

      <div className="addUser-checkbox-group">
        {loadingOption ? (
          <Loader />
        ) : (
          <>
              {/* Select All Button */}
              {!loadingOption && visibleOptions.length > 0 && (
                <label className="addUser-checkbox-option">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={handleSelectAll}
                    style={{ fontWeight: "bold" }}
                  />
                  <span style={{ fontWeight: "bold" }}>
                    {allVisibleSelected ? "Deselect All" : "Select All"}
                  </span>
                </label>
              )}
            {visibleOptions.map((option) => (
              <label key={option.value} className="addUser-checkbox-option">
                <input
                  type="checkbox"
                  checked={selected.includes(option.value)}
                  onChange={() => toggleOption(option.value)}
                />
                {option.label}
              </label>
            ))}

            {visibleOptions.length === 0 && (
              <div className="addUser-checkbox-empty">No matches found.</div>
            )}
          </>
        )}
      </div>

      <div className="addUser-error">{error ?? ""}</div>
    </div>
  );
};
